/**
 * Fishing Debug HUD
 * Real-time overlay showing fishing state, sweet spot positions, and click timing.
 * Polls window.__fishingDebug (exposed by FishButton in dev mode).
 * Toggle via Debug Menu → "Fishing HUD" button.
 */

import { useState, useEffect } from 'react';

interface FishingDebugState {
  isFishing: boolean;
  progress: number;
  totalDuration: number;
  sweetSpotStart: number;
  sweetSpotEnd: number;
  sweetSpotCenter: number;
  goodZoneStart: number;
  goodZoneEnd: number;
  sweetSpotHit: boolean;
  lastClickResult: 'perfect' | 'good' | 'miss' | null;
  constants: {
    SWEET_SPOT_SIZE: number;
    GOOD_ZONE_MARGIN: number;
    PERFECT_BONUS: number;
    GOOD_BONUS: number;
  };
}

const POLL_INTERVAL_MS = 50; // 20fps — fast enough for visual debugging

export function FishingDebugHUD({ onClose }: { onClose: () => void }) {
  const [state, setState] = useState<FishingDebugState | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      const debug = window.__fishingDebug as FishingDebugState | undefined;
      setState(debug ?? null);
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  const fmt = (n: number) => n.toFixed(3);
  const pct = (n: number) => `${(n * 100).toFixed(1)}%`;

  // Determine where progress is relative to zones
  const getZoneLabel = (s: FishingDebugState): string => {
    if (!s.isFishing) return 'IDLE';
    const p = s.progress;
    if (p >= s.sweetSpotStart && p <= s.sweetSpotEnd) return 'PERFECT ZONE';
    if (p >= s.goodZoneStart && p <= s.goodZoneEnd) return 'GOOD ZONE';
    return 'MISS ZONE';
  };

  const getZoneColor = (label: string): string => {
    switch (label) {
      case 'PERFECT ZONE': return 'text-yellow-300';
      case 'GOOD ZONE': return 'text-blue-300';
      case 'MISS ZONE': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const resultColor = (r: string | null): string => {
    switch (r) {
      case 'perfect': return 'text-yellow-300';
      case 'good': return 'text-blue-300';
      case 'miss': return 'text-red-400';
      default: return 'text-gray-500';
    }
  };

  return (
    <div
      className="fixed bottom-20 right-2 z-50 bg-black/90 border border-green-500/50 rounded-lg p-3 font-mono text-xs w-64 shadow-xl"
      style={{ backdropFilter: 'blur(4px)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-green-400 font-bold text-sm">🎣 Fishing HUD</span>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-white text-sm px-1 cursor-pointer"
        >
          ✕
        </button>
      </div>

      {!state ? (
        <p className="text-gray-500">Waiting for fishing data...</p>
      ) : (
        <div className="space-y-1.5">
          {/* Status */}
          <Row label="Status" value={state.isFishing ? 'FISHING' : 'IDLE'}
            className={state.isFishing ? 'text-green-400' : 'text-gray-400'} />

          {/* Progress bar + value */}
          {state.isFishing && (
            <>
              <Row label="Progress" value={pct(state.progress)} className="text-white" />

              {/* Visual progress bar with zone markers */}
              <div className="relative h-4 bg-gray-800 rounded-full overflow-hidden border border-gray-700 mt-1">
                {/* Good zone background */}
                <div
                  className="absolute top-0 bottom-0 bg-blue-500/20"
                  style={{
                    left: pct(Math.max(0, state.goodZoneStart)),
                    width: pct(state.goodZoneEnd - state.goodZoneStart),
                  }}
                />
                {/* Perfect zone background */}
                <div
                  className="absolute top-0 bottom-0 bg-yellow-500/30"
                  style={{
                    left: pct(state.sweetSpotStart),
                    width: pct(state.constants.SWEET_SPOT_SIZE),
                  }}
                />
                {/* Progress indicator */}
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-white"
                  style={{ left: pct(state.progress) }}
                />
              </div>

              {/* Zone status */}
              {(() => {
                const label = getZoneLabel(state);
                return <Row label="Zone" value={label} className={getZoneColor(label)} />;
              })()}

              <Row label="Duration" value={`${state.totalDuration.toFixed(1)}s`} className="text-gray-300" />
            </>
          )}

          {/* Divider */}
          <div className="border-t border-gray-700 my-1" />

          {/* Sweet spot info */}
          <div className="text-gray-500 text-[10px] font-bold uppercase tracking-wider">Sweet Spot</div>
          <Row label="Perfect" value={`${fmt(state.sweetSpotStart)} → ${fmt(state.sweetSpotEnd)}`} className="text-yellow-400" />
          <Row label="Good" value={`${fmt(state.goodZoneStart)} → ${fmt(state.goodZoneEnd)}`} className="text-blue-400" />
          <Row label="Center" value={fmt(state.sweetSpotCenter)} className="text-gray-300" />

          {/* Click result */}
          <div className="border-t border-gray-700 my-1" />
          <Row label="Clicked" value={state.sweetSpotHit ? 'YES' : 'NO'}
            className={state.sweetSpotHit ? 'text-green-400' : 'text-gray-500'} />
          <Row label="Result" value={state.lastClickResult ?? '—'}
            className={resultColor(state.lastClickResult)} />

          {/* Constants */}
          <div className="border-t border-gray-700 my-1" />
          <div className="text-gray-500 text-[10px] font-bold uppercase tracking-wider">Constants</div>
          <Row label="Perfect %" value={pct(state.constants.SWEET_SPOT_SIZE)} className="text-gray-400" />
          <Row label="Good margin" value={pct(state.constants.GOOD_ZONE_MARGIN)} className="text-gray-400" />
          <Row label="Perfect bonus" value={`+${pct(state.constants.PERFECT_BONUS)}`} className="text-yellow-400" />
          <Row label="Good bonus" value={`+${pct(state.constants.GOOD_BONUS)}`} className="text-blue-400" />
        </div>
      )}
    </div>
  );
}

function Row({ label, value, className = 'text-white' }: { label: string; value: string; className?: string }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-gray-500">{label}</span>
      <span className={className}>{value}</span>
    </div>
  );
}
